import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { getExternalProviderHealthSummary } from '../api/providersApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { SesHealthDetailsPanel } from '../components/SesHealthDetailsPanel';
import { Button } from '../components/ui/Button';
import { translateProviderHealthMessage } from '../utils/providerHealthMessages';

const { semantic } = colorTokens;

const TYPE_KEYS = ['storage', 'email', 'sms', 'push'];

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const messageSx = {
  fontFamily: typographyTokens.fontFamily,
  fontSize: 13,
  lineHeight: '20px',
  color: semantic.text.primary,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  maxWidth: 420,
};

const compactCellSx = {
  fontFamily: typographyTokens.fontFamily,
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.secondary,
  whiteSpace: 'nowrap',
};

function formatDate(value) {
  if (!value) {
    return null;
  }

  return new Date(value).toLocaleString();
}

function healthTone(status) {
  if (status === 'ok' || status === 'healthy' || status === 'success') return 'Success';
  if (status === 'failed' || status === 'error') return 'Danger';
  if (status === 'skipped' || status === 'disabled') return 'Warning';
  return 'Neutral';
}

export function ExternalProviderHealthPage() {
  const { t } = useTranslation(['providers', 'common']);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const emptyValue = t('common:empty_value');
  const healthChecks = summary?.recent_health_checks || [];
  const usageLogs = summary?.recent_usage_logs || [];
  const healthCount = healthChecks.length;
  const usageCount = usageLogs.length;

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getExternalProviderHealthSummary();
      setSummary(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const healthColumns = useMemo(() => [
    {
      id: 'provider_type',
      label: t('common:type'),
      render: (row) => (
        <Typography sx={monoSx}>{row.provider_type || emptyValue}</Typography>
      ),
    },
    {
      id: 'provider_key',
      label: t('common:key'),
      render: (row) => (
        <Typography sx={monoSx}>{row.provider_key || emptyValue}</Typography>
      ),
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => (
        row.status
          ? (
            <StatusBadge
              status={row.status}
              category="generic"
              apiLabel={row.status}
              size="Sm"
              tone={healthTone(row.status)}
            />
          )
          : emptyValue
      ),
    },
    {
      id: 'message',
      label: t('common:message'),
      render: (row) => {
        const message = translateProviderHealthMessage(t, row) || emptyValue;

        return (
          <Stack spacing={0.5} sx={{ maxWidth: 420, minWidth: 0 }}>
            <Typography sx={messageSx}>{message}</Typography>
            {row.provider_key === 'ses' && row.details && (
              <SesHealthDetailsPanel details={row.details} />
            )}
          </Stack>
        );
      },
    },
    {
      id: 'duration_ms',
      label: t('providers:health.columns.duration_ms'),
      render: (row) => (
        <Typography sx={compactCellSx}>
          {row.duration_ms ?? emptyValue}
        </Typography>
      ),
    },
    {
      id: 'created_at',
      label: t('providers:health.columns.checked_at'),
      render: (row) => (
        <Typography sx={compactCellSx}>
          {formatDate(row.created_at) || emptyValue}
        </Typography>
      ),
    },
  ], [emptyValue, t]);

  const usageColumns = useMemo(() => [
    {
      id: 'provider_type',
      label: t('common:type'),
      render: (row) => (
        <Typography sx={monoSx}>{row.provider_type || emptyValue}</Typography>
      ),
    },
    {
      id: 'provider_key',
      label: t('common:key'),
      render: (row) => (
        <Typography sx={monoSx}>{row.provider_key || emptyValue}</Typography>
      ),
    },
    {
      id: 'operation',
      label: t('providers:health.columns.operation'),
      render: (row) => (
        <Typography sx={monoSx}>{row.operation || emptyValue}</Typography>
      ),
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => (
        row.status
          ? (
            <StatusBadge
              status={row.status}
              category="generic"
              apiLabel={row.status}
              size="Sm"
              tone={healthTone(row.status)}
            />
          )
          : emptyValue
      ),
    },
    {
      id: 'failure_reason',
      label: t('providers:health.columns.failure'),
      render: (row) => (
        <Typography sx={messageSx}>
          {row.failure_reason || emptyValue}
        </Typography>
      ),
    },
    {
      id: 'created_at',
      label: t('providers:health.columns.at'),
      render: (row) => (
        <Typography sx={compactCellSx}>
          {formatDate(row.created_at) || emptyValue}
        </Typography>
      ),
    },
  ], [emptyValue, t]);

  const tableSx = {
    overflowX: 'auto',
    width: '100%',
    '& table': {
      width: '100%',
      minWidth: '100%',
    },
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('providers:health.title')}
        subtitle={t('providers:health.subtitle')}
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadSummary} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('providers:health.load_failed')}
        </Alert>
      )}

      {loading ? (
        <>
          <SectionCard variant="Default" sx={{ mb: 2.5 }}>
            <LoadingSkeleton type="CardGrid" rows={4} />
          </SectionCard>
          <SectionCard variant="Default" noPadding sx={{ width: '100%', mb: 2.5 }}>
            <TableToolbar
              mode="Default"
              title={t('providers:health.recent_health_checks')}
            />
            <LoadingSkeleton type="Table" embedded rows={3} columns={6} />
          </SectionCard>
          <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('providers:health.recent_usage_logs')}
            />
            <LoadingSkeleton type="Table" embedded rows={3} columns={6} />
          </SectionCard>
        </>
      ) : !error && summary ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 2,
              width: '100%',
              mb: 2.5,
            }}
          >
            {TYPE_KEYS.map((type) => {
              const item = summary.by_type?.[type] || {};

              return (
                <MetricCard
                  key={type}
                  value={item.provider_key || emptyValue}
                  label={`${t(`providers:health.types.${type}`)} · ${t('providers:health.metric_helper', {
                    active: item.is_active ? t('providers:health.active') : t('providers:health.inactive'),
                    health: item.last_health_status || t('common:not_available'),
                  })}`}
                />
              );
            })}
          </Box>

          <SectionCard variant="Default" sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontFamily: typographyTokens.fontFamily,
                fontSize: 13,
                lineHeight: '20px',
                color: semantic.text.secondary,
              }}
            >
              {t('providers:health.summary', {
                enabled: summary.providers_enabled
                  ? t('providers:health.enabled_yes')
                  : t('providers:health.enabled_no'),
                checks: summary.health_checks_enabled
                  ? t('providers:health.checks_enabled')
                  : t('providers:health.checks_disabled'),
              })}
            </Typography>
          </SectionCard>

          <SectionCard variant="Default" noPadding sx={{ width: '100%', mb: 2.5 }}>
            <TableToolbar
              mode="Default"
              title={t('providers:health.recent_health_checks')}
              meta={(
                <StatusBadge
                  status="list"
                  category="generic"
                  apiLabel={t('providers:health.toolbar_count', { count: healthCount })}
                  size="Sm"
                  tone="Neutral"
                />
              )}
            />
            <DataTable
              type="Default"
              embedded
              columns={healthColumns}
              rows={healthChecks}
              getRowKey={(row) => (
                row.id
                || `${row.provider_type}-${row.provider_key}-${row.status}-${row.created_at}-${row.duration_ms}`
              )}
              emptyMessage={t('providers:health.no_health_checks')}
              sx={tableSx}
            />
          </SectionCard>

          <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
            <TableToolbar
              mode="Default"
              title={t('providers:health.recent_usage_logs')}
              meta={(
                <StatusBadge
                  status="list"
                  category="generic"
                  apiLabel={t('providers:health.usage_toolbar_count', { count: usageCount })}
                  size="Sm"
                  tone="Neutral"
                />
              )}
            />
            <DataTable
              type="Default"
              embedded
              columns={usageColumns}
              rows={usageLogs}
              getRowKey={(row) => (
                row.id
                || `${row.provider_type}-${row.provider_key}-${row.operation}-${row.status}-${row.created_at}`
              )}
              emptyMessage={t('providers:health.no_usage_logs')}
              sx={tableSx}
            />
          </SectionCard>
        </>
      ) : null}
    </Box>
  );
}
